<template>
  <NavMenu v-if="!QRCode" :isCampaignDonate="true" :campaign="campaign" />
  <GlobalWrapper
    :style="{ backgroundImage: 'url(' + campaign?.background + ')' }"
    class="pb-10 pt-4 bg-cover min-h-screen sm:pb-12 sm:pt-5"
  >
    <div
      class="mx-auto donor-donate-shell"
      :class="{ disabled: loading || !campaignActive }"
      :style="donationFormOpacityStyle"
    >
      <div class="donor-donate-toolbar">
        <img :src="npo?.logo" alt="Non-profit logo" class="donor-donate-toolbar__logo" />

        <div v-if="userId" class="donor-donate-toolbar__actions">
          <BaseButton
            v-if="!isQRCodeOnly"
            variant="secondary"
            size="xs"
            block
            :class="loading || !campaignActive ? 'disabled' : null"
            @click.prevent="toggleQRCodeModal"
          >
            Share QR Code
          </BaseButton>
          <BaseButton
            variant="secondary"
            size="xs"
            block
            :class="loading || !campaignActive ? 'disabled' : null"
            @click="router.push({ name: 'CampaignList' })"
          >
            View Dashboard
          </BaseButton>
          <div v-if="showStopCounter" class="donor-stop-counter">
            <p class="text-sm font-semibold text-gray-900">Stops today</p>
            <div class="donor-stop-counter__controls">
              <button
                type="button"
                aria-label="Decrease stop count"
                class="px-2 text-xl leading-none text-gray-900 hover:text-gray-700 disabled:opacity-50"
                :disabled="stopCountLoading"
                @click="changeStopCount(-1)"
              >
                −
              </button>
              <span
                class="mx-3 min-w-[2rem] text-center text-lg font-semibold tabular-nums text-gray-900"
              >
                {{ stopCount }}
              </span>
              <button
                type="button"
                aria-label="Increase stop count"
                class="px-2 text-xl leading-none text-gray-900 hover:text-gray-700 disabled:opacity-50"
                :disabled="stopCountLoading"
                @click="changeStopCount(1)"
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>

      <Spinner v-show="loading" class="mt-10" />
      <div v-if="!loading && isQRCodeOnly" class="donor-form-qr-panel mt-4 w-full">
        <h2 class="text-xl font-semibold text-gray-900 sm:text-2xl">Scan the QR code to donate!</h2>
        <div class="mt-6 flex justify-center sm:mt-8">
          <img
            v-if="qrCodeImgUrl"
            :src="qrCodeImgUrl"
            alt="QR code to donate"
            class="inline-block h-44 w-44 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm sm:h-56 sm:w-56"
          />
          <Spinner v-else />
        </div>
      </div>
      <div v-show="!loading && !isQRCodeOnly" class="donor-donate-content relative mt-2">
        <DonorFormProgress :current-step="progressStep" />

        <div
          v-if="isResubmitMode"
          class="donor-donate-alert donor-donate-alert--warning"
          role="status"
        >
          This donation is pending because the payment processor did not respond. Review the
          pre-filled details, click through each step, and submit again.
        </div>

        <!-- Step 1: Personal Details -->
        <div v-show="stepNumber === 0">
          <DonorFormPersonal
            :key="personalFormKey"
            :showClearForm="hasReturnedFromPaymentPage"
            :initial-values="personalPrefill"
            @personalData="setPersonalData"
            @stepNumber="getStepNumber"
            @userAge="setUserAge"
            @clearForm="clearForm"
            :lowestAge="lowestAge"
          />
        </div>

        <!-- Step 2: Contact Details / Terms & Agreement  -->
        <div v-show="stepNumber === 1">
          <template
            v-if="
              paymentProviders[paymentProvider] !== EveryAction &&
              paymentProviders[paymentProvider] !== SpringBoard &&
              paymentProviders[paymentProvider] !== NAACPCustomAPI
            "
          >
            <DonorFormContact
              :key="contactFormKey"
              :backStep="backStepForCurrentStep"
              :initial-values="contactPrefill"
              :npo-identifier="npo?.identifier"
              @contactData="getContactData"
              @stepNumber="getStepNumber"
            />
          </template>
          <!-- Skip terms page if campaign does NOT have monthly frequency -->
          <template v-else-if="!hasMonthlyFrequency">
            <DonorFormContact
              :key="contactFormKey"
              :backStep="backStepForCurrentStep"
              :initial-values="contactPrefill"
              :npo-identifier="npo?.identifier"
              @contactData="getContactData"
              @stepNumber="getStepNumber"
            />
          </template>
          <template v-else>
            <div class="donor-form-card">
              <div class="donor-form-card__body space-y-4">
                <div class="donor-form-header">
                  <div class="donor-form-header__icon">
                    <DocumentTextIcon class="h-5 w-5 text-primaryAccent" aria-hidden="true" />
                  </div>
                  <div class="donor-form-header__text">
                    <h2 class="donor-form-title">Terms &amp; Agreement</h2>
                    <p class="donor-form-subtitle">Please review and accept to continue.</p>
                  </div>
                </div>

                <label for="terms" class="flex cursor-pointer items-start gap-3">
                  <input
                    id="terms"
                    text="terms"
                    type="checkbox"
                    class="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-primary focus:ring-primary"
                    v-model="termsAgreement.value"
                    required
                  />
                  <span
                    v-if="paymentProviders[paymentProvider] === SpringBoard"
                    class="donor-form-terms-card block flex-1"
                  >
                    I am at least 25 years old and I am agreeing to contribute financial resources
                    to International Rescue Committee of my own free will. I understand that this is
                    an ongoing gift, vital to the International Rescue Committee’s mission to
                    respond to the world’s worst humanitarian crises and help people to survive and
                    rebuild their lives, and my gift will continue until I notify International
                    Rescue Committee of any change.
                    <br /><br />
                    I understand that I have spoken to a paid fundraiser employed by an agency paid
                    a fee and authorized by International Rescue Committee to conduct this type of
                    face-to-face fundraising work. I authorize the selected amount to be paid to
                    International Rescue Committee from my credit/bank as indicated above. I agree
                    to allow the International Rescue Committee and their authorized partner agency
                    to contact me by telephone, text, email, or mail regarding my donation. I give
                    consent to International Rescue Committee ownership rights to my photo or image,
                    to reproduce and use for any purpose worldwide in furtherance of International
                    Rescue Committee’s mission without exception of compensation to me now or in the
                    future. If, for any reason, I decide to cancel my contribution, I may do so at
                    any time by calling International Rescue Committee at 1-855-973-7283. A record
                    of my payment(s) will appear on my credit card or bank statement and will serve
                    as a receipt.
                  </span>
                  <span
                    v-else-if="paymentProviders[paymentProvider] === NAACPCustomAPI"
                    class="donor-form-terms-card block flex-1"
                  >
                    I understand that I have spoken to a paid fundraiser, employed by New Canvassing
                    Experience Inc., an agency paid a fee by NAACP Empowerment Programs, Inc. to
                    conduct this type of face-to-face fundraising work. I authorize the chosen
                    amount to be debited from my bank account, or charged to my credit card account,
                    and paid to NAACP Empowerment Programs, Inc. immediately, and authorize
                    subsequent payments to be processed at the agreed frequency following the
                    initial payment, until further notice from me to NAACP. I understand that this
                    is an ongoing gift. If, for any reason, I need to update or suspend my
                    contributions, I may do so at any time by contacting NAACP. Note: New Canvassing
                    Experience Inc., is not permitted to take cash or any other form of donation
                    other than credit/debit card or direct debit contributions on behalf of NAACP.
                    <br />
                    I am at least 25 years old and I am agreeing to contribute financial resources
                    to the NAACP of my own free will. A record of my payment(s) will appear on my
                    credit card or bank statement and will serve as a receipt. Contributions or
                    gifts to NAACP Empowerment Programs, Inc. are tax deductible to the full extent
                    of the law. Your contribution will not be earmarked for any purpose and will
                    generally support our activities to fight for an inclusive community, rooted in
                    liberation, where all can exercise their civil and human rights without
                    discrimination.
                    <br />
                    By providing your contribution you are affirming that you are a U.S. citizen or
                    a permanent legal resident of the United States, and that you have used your own
                    funds to make this gift and not the funds of another person or corporation.
                    Sorry, no contributions from business corporations may be accepted. Information
                    about your contribution may be made publicly available.
                  </span>
                  <span v-else class="donor-form-terms-card block flex-1">
                    I understand that I have spoken to a paid fundraiser employed by New Canvassing
                    Experience, Inc., an agency paid a fee by {{ npo?.name }} to conduct this
                    face-to-face fundraising campaign. I authorize the agreed upon amount to be
                    debited from my bank account or charged to my credit card account, and paid to
                    {{ npo?.name }} immediately, and authorize subsequent payments to be processed
                    at the agreed upon frequency following initial payment, until further notice
                    from me to {{ npo?.name }}. If, for any reason, I need to update or suspend my
                    contributions, I may do so at any time by contacting {{ npo?.name }}
                    <br /><br />
                    Note: New Canvassing Experience Inc. is not permitted to take cash or any other
                    form of donatino other than credit/debit card or direct debit contributions on
                    behalf of CeaseFire PA.
                    <br /><br />
                    Contributions or gifts to CeaseFire PA are not tax deductible as charitable
                    contributions or as business expenses under IRC Section 162(e).
                  </span>
                </label>
              </div>

              <div class="donor-form-actions">
                <BaseButton type="button" variant="secondary" block @click="getStepNumber(0)">
                  Go Back
                </BaseButton>
                <BaseButton
                  block
                  :disabled="!termsAgreement.value"
                  @click="
                    getStepNumber(
                      paymentProviders[paymentProvider] === SpringBoard ||
                        NAACPCustomAPI ||
                        EngagingNetwork
                        ? 1.5
                        : 2,
                    )
                  "
                >
                  Accept Terms
                </BaseButton>
              </div>
            </div>
          </template>
        </div>

        <!-- step 2.5 Contact Details -->
        <div v-show="stepNumber === 1.5">
          <DonorFormContact
            :key="contactFormKey"
            :backStep="backStepForCurrentStep"
            :initial-values="contactPrefill"
            :npo-identifier="npo?.identifier"
            @contactData="getContactData"
            @stepNumber="getStepNumber"
          />
        </div>

        <!-- Step 3: Payment Details  -->
        <div v-show="stepNumber === 2">
          <div
            v-if="
              bbmsCheckoutRetryMessage &&
              [BBMSCheckout, BBMSCheckoutInline].includes(paymentProviders[paymentProvider])
            "
            class="donor-donate-alert donor-donate-alert--error"
            role="alert"
          >
            <p class="font-medium">Payment could not be completed</p>
            <p class="mt-1">{{ bbmsCheckoutRetryMessage }}</p>
            <p class="mt-2">Please review your payment details and click Submit Donation again.</p>
          </div>
          <DonorFormPayment
            :key="paymentFormKey"
            :backStep="backStepForCurrentStep"
            v-if="paymentProviders[paymentProvider] !== EveryAction"
            @submitForm="onSubmitForms"
            @checkoutStarted="onBBMSCheckoutInlineStarted"
            @checkoutError="onBBMSCheckoutInlineError"
            @stepNumber="getStepNumber"
            :checkout-config="checkoutConfig"
            :campaign-api-url="campaignApiUrl"
            :campaign-id="campaignId"
            :npoName="npo?.name"
            :frequency-options="filteredFrequencies"
            :emailOptIn="emailOptIn"
            :paymentProviders="paymentProviders[paymentProvider]"
            :mobilePhone="mobilePhone || ''"
            :email="email || ''"
            :is-payment-step-visible="stepNumber === 2"
            :donorVerificationEnabled="campaign?.donorVerificationEnabled ?? true"
            :payment-prefill="paymentPrefill"
            :preserve-unique-id="isResubmitMode"
          />

          <div class="flex justify-center" v-else>
            <div ref="everyActionContainer"></div>
            <!-- <button @click="getEaFormData">Get Data</button> -->
          </div>
        </div>
      </div>
    </div>
    <Modal
      headerText="Thank you for your donation!"
      ctaText="Close and Reset Form"
      :subheaderText="donateSuccessMessage"
      :modalOpen="successModalOpen"
      @closeModal="toggleSuccessModal"
      :showSuccessIcon="true"
      wide
    />
    <Modal
      headerText="Donation Failed :("
      ctaText="Close and Try Again"
      :subheaderText="'Reason: ' + donateFailureMessage"
      :modalOpen="failureModalOpen"
      @closeModal="toggleFailureModal"
      :showFailureIcon="true"
    />
    <Modal
      headerText="Scan the QR code to donate!"
      ctaText="Close"
      :modalOpen="qrCodeModalOpen"
      @closeModal="toggleQRCodeModal"
      :modalImgUrl="qrCodeImgUrl"
    />
    <Toast v-if="loading" text="Loading..." />
    <Toast v-else-if="!loading && !campaignActive" :text="disabledMessage" />
  </GlobalWrapper>
</template>

<script>
import { ref, onBeforeMount, computed, onUnmounted, nextTick } from 'vue'
import { useToast } from 'vue-toastification'
import { useStore } from 'vuex'
import GlobalWrapper from '../components/GlobalWrapper'
import DonorFormPersonal from '@/components/DonorFormPersonal'
import DonorFormContact from '@/components/DonorFormContact'
import DonorFormPayment from '@/components/DonorFormPayment'
import Modal from '@/components/Modal'
import Toast from '@/components/Toast'
import Spinner from '@/components/Spinner'
import { CampaignApi } from '@/services/campaign/'
import { everyAction } from '@/composables/everyAction'
import {
  EngagingNetwork,
  EveryAction,
  SpringBoard,
  paymentProviders,
  NAACPCustomAPI,
  BBMSCheckout,
  BBMSCheckoutInline,
} from '@/services/npo'
import { donationActions } from '@/composables/donationSubmissions'
import { useRoute } from 'vue-router'
import { sample } from '@/services/frequencies/sample'
import { useRouter } from 'vue-router'
import NavMenu from '@/components/NavMenu'
import DonorFormProgress from '@/components/DonorFormProgress.vue'
import { DocumentTextIcon } from '@heroicons/vue/20/solid'
import { ONETIME, MONTHLY } from '@/services/frequencies/options'
import { termsAgreementField } from '@/services/users'
import { WorkedHoursApi } from '@/services/workingHours'
import { DonationApi } from '@/services/donations/api'
import { resolveResubmitFormState } from '@/utils/resubmitPrefill'
import {
  formTokenQuery,
  renderQrCodeDataUrl,
  requestDonorFormToken,
  storeFormToken,
} from '@/services/formAccess'

export default {
  name: 'CampaignDonate',
  components: {
    GlobalWrapper,
    DonorFormPersonal,
    DonorFormContact,
    DonorFormPayment,
    Modal,
    Toast,
    Spinner,
    NavMenu,
    DonorFormProgress,
    DocumentTextIcon,
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const store = useStore()
    const toast = useToast()
    const {
      campaign,
      onSubmitForms,
      onSubmitFormsAlt,
      campaignId,
      fundraiserId,
      formToken,
      loading,
      donorId,
      getMaskedPan,
      toggleFailureModal,
      toggleSuccessModal,
      successModalOpen,
      failureModalOpen,
      donateSuccessMessage,
      donateFailureMessage,
      createDonor,
      termsAgreement,
      registerResetDonationForm,
      bbmsCheckoutRetryMessage,
      onBBMSCheckoutInlineStarted,
      onBBMSCheckoutInlineError,
    } = donationActions()
    const userToken = store.getters.token
    const userId = store.getters.user ? store.getters.user.id : ''
    const isFundraiser = store.getters.isFundraiser
    const npo = ref(null)
    // NPOs can make the form background see-through so the campaign picture shows
    const donationFormOpacityStyle = computed(() => {
      const opacity = npo.value?.donationFormOpacity
      return opacity == null ? {} : { '--donor-form-opacity': opacity / 100 }
    })
    const age = ref(18)
    const stepNumber = ref(0)
    const acceptedTerms = ref(true)
    const hasReturnedFromPaymentPage = ref(false)
    const personalFormKey = ref(0)
    const contactFormKey = ref(0)
    const paymentFormKey = ref(0)
    const isResubmitMode = ref(false)
    const personalPrefill = ref(null)
    const contactPrefill = ref(null)
    const paymentPrefill = ref(null)
    const resubmitFormReady = ref(!route.query.resubmit)

    const qrCodeModalOpen = ref(false)
    const qrCodeImgUrl = ref('')
    const campaignQueryParams = formTokenQuery(formToken)

    const campaignName = ref(null)
    const emailOptIn = ref(false)
    const campaignApiUrl = `/campaigns/${campaignId}/${campaignQueryParams}`
    const mobilePhone = ref(null)
    const email = ref(null)
    const QRCode = ref(route.query.from_qrcode)
    const stopCount = ref(0)
    const stopCountLoading = ref(false)
    const showStopCounter = computed(
      () => !!userId && !QRCode.value && !!campaign.value?.stopCountEnabled,
    )
    // QR-only mode
    const isQRCodeOnly = computed(
      () => !!userId && !QRCode.value && !!campaign.value?.qrCodeOnlyEnabled,
    )

    const checkoutConfig = computed(() => {
      const provider = campaign.value?.latestPaymentProvider
      if (!provider) return null
      return {
        environmentId: provider.environmentId,
        paymentConfigurationId: provider.paymentConfigurationId,
        checkoutZone: provider.checkoutZone,
        recaptchaSiteKey: provider.recaptchaSiteKey,
      }
    })

    const filteredFrequencies = computed(() =>
      campaign.value?.frequencies.filter(
        (frequency) =>
          frequency.minAge <= age.value && frequency.campaign.includes(campaign.value.id),
      ),
    )

    // Check if campaign only has one-time frequency available
    const isOneTimeOnly = computed(() => {
      if (!campaign.value?.frequencies || filteredFrequencies.value?.length === 0) {
        return false
      }
      // Check if all available frequencies are one-time only
      return filteredFrequencies.value.every(
        (frequency) => frequency.value?.toLowerCase() === ONETIME.toLowerCase(),
      )
    })

    // Check if campaign has monthly frequency available
    const hasMonthlyFrequency = computed(() => {
      if (!campaign.value?.frequencies || filteredFrequencies.value?.length === 0) {
        return false
      }
      // Check if any available frequency is monthly
      return filteredFrequencies.value.some(
        (frequency) => frequency.value?.toLowerCase() === MONTHLY.toLowerCase(),
      )
    })

    const lowestAge = computed(() => {
      if (campaign.value) {
        const ages = campaign.value.frequencies.map((frequency) => frequency.minAge)
        return Math.min(...ages)
      }
      return age.value
    })
    const setUserAge = function (newAge) {
      age.value = newAge
    }
    const {
      paymentProvider,
      everyActionContainer,
      everyActionProvider,
      calledPostOrPre,
      getEaFormData,
    } = everyAction()

    onBeforeMount(async () => {
      loading.value = true
      try {
        campaign.value = await CampaignApi.csc.customRetrieve({
          campaign: campaignId,
          formToken,
        })
        npo.value = campaign.value.npo
        campaignName.value = campaign.value.name

        /**
         * TODO: Here we will check the payment processor
         */
        paymentProvider.value = campaign.value.latestPaymentProvider?.paymentProvider
        if (showStopCounter.value) {
          await fetchStopCount()
        }
        if (isQRCodeOnly.value) {
          await getQRCode(256)
        }
        const resubmitId = route.query.resubmit
        if (resubmitId) {
          try {
            const resubmitData = await DonationApi.csc.getResubmitData({ donationId: resubmitId })
            const built = resolveResubmitFormState(resubmitData, campaign.value?.frequencies || [])
            localStorage.setItem('DonorData', JSON.stringify(built.donorData))
            personalPrefill.value = { ...built.personalPrefill }
            contactPrefill.value = { ...built.contactPrefill }
            paymentPrefill.value = { ...built.paymentPrefill }
            isResubmitMode.value = true
            emailOptIn.value = built.donorData.donor.isEmailPreferred
            mobilePhone.value = built.donorData.donor.mobilePhone
            email.value = built.donorData.donor.email
            await nextTick()
            personalFormKey.value += 1
            contactFormKey.value += 1
            paymentFormKey.value += 1
          } catch (resubmitError) {
            console.error('Failed to load resubmit data:', resubmitError)
            const message =
              resubmitError?.response?.data?.detail ||
              resubmitError?.response?.data?.[0] ||
              'Could not load pending donation data. Please try again or contact support.'
            toast.error(message)
          } finally {
            resubmitFormReady.value = true
          }
        }
      } catch (e) {
        campaign.value = null
        npo.value = null
      } finally {
        loading.value = false
      }
    })
    const getStepNumber = (data) => {
      if (stepNumber.value === 0 && (data === 1 || data === 1.5 || data === 2)) {
        hasReturnedFromPaymentPage.value = true
      }
      stepNumber.value = data
      document.body.scrollTop = document.documentElement.scrollTop = 0
    }

    // Back button target
    const backStepForCurrentStep = computed(() => {
      if (stepNumber.value === 1) return 0
      if (stepNumber.value === 1.5) return 1
      if (stepNumber.value === 2) {
        if (hasMonthlyFrequency.value && paymentProviders[paymentProvider.value] === SpringBoard) {
          return 1.5
        }
        return 1
      }
      return 0
    })

    const progressStep = computed(() => {
      if (stepNumber.value === 0) return 1
      if (stepNumber.value === 1 || stepNumber.value === 1.5) return 2
      if (stepNumber.value === 2) return 3
      return 1
    })

    const clearForm = () => {
      localStorage.removeItem('DonorData')
      stepNumber.value = 0
      hasReturnedFromPaymentPage.value = false
      personalFormKey.value += 1
      contactFormKey.value += 1
      paymentFormKey.value += 1
      emailOptIn.value = false
      mobilePhone.value = null
      email.value = null
      if (typeof termsAgreement.value !== 'object' || termsAgreement.value === null) {
        termsAgreement.value = termsAgreementField
      }
      termsAgreement.value.value = true
    }

    registerResetDonationForm(clearForm)

    const getFundraiserLocation = () => {
      return new Promise((resolve) => {
        if (!('geolocation' in navigator)) {
          resolve(null)
          return
        }
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            })
          },
          () => resolve(null),
          {
            enableHighAccuracy: false,
            timeout: 10000,
            maximumAge: 60000,
          },
        )
      })
    }

    const getQRCode = async (size = 150) => {
      const coords = await getFundraiserLocation()
      // The donor's device has no login, so the link carries a signed, expiring grant
      // for this fundraiser and campaign rather than the fundraiser's id.
      const token = await requestDonorFormToken(campaignId)
      let url = `${location.host}${route.fullPath}?form_token=${encodeURIComponent(
        token,
      )}&from_qrcode=true`
      if (coords) {
        url += `&lat=${encodeURIComponent(coords.lat)}&lng=${encodeURIComponent(coords.lng)}`
      }
      // Rendered locally: the URL is a live credential and must not be sent to an
      // external image service to be drawn.
      qrCodeImgUrl.value = await renderQrCodeDataUrl(url, size)
    }

    const toggleQRCodeModal = async () => {
      if (!qrCodeModalOpen.value) {
        await getQRCode()
      }
      qrCodeModalOpen.value = !qrCodeModalOpen.value
    }

    const fetchStopCount = async () => {
      try {
        stopCountLoading.value = true
        const response = await WorkedHoursApi.csc.getStopCount({ campaign: campaignId })
        stopCount.value = response?.stop_count ?? 0
      } catch (error) {
        console.error('Error fetching stop count:', error)
      } finally {
        stopCountLoading.value = false
      }
    }

    const changeStopCount = async (delta) => {
      if (stopCountLoading.value) return
      try {
        stopCountLoading.value = true
        const response = await WorkedHoursApi.csc.updateStopCount({
          campaign: campaignId,
          delta,
        })
        stopCount.value = response?.stop_count ?? stopCount.value
      } catch (error) {
        console.error('Error updating stop count:', error)
      } finally {
        stopCountLoading.value = false
      }
    }

    const campaignActive = computed(() => {
      return (
        campaign.value?.active &&
        campaign.value?.latestPaymentProvider &&
        campaign.value?.collectingDonations
      )
    })

    const disabledMessage = computed(() => {
      let reasons = []
      let message = ''

      if (!campaign.value) {
        if (isFundraiser) {
          message =
            'You do not have access to this campaign. Please contact your organization admin for further assistance.'
        } else {
          message =
            'There was an error retrieving the campaign details. Please refresh the page or contact an admin for further assistance.'
        }
      } else {
        message = 'This campaign is inactive.'

        !campaign.value?.collectingDonations &&
          reasons.push(" It's marked as 'Not Accepting Donations'.")

        !campaign.value?.latestPaymentProvider &&
          reasons.push(" There's no payment provider attached.")

        if (reasons.length > 1) {
          message += reasons.join(' and')
        } else if (reasons.length === 1) {
          message += reasons[0]
        }
      }
      return message
    })

    async function setPersonalData(data) {
      // debugger
      localStorage.setItem('DonorData', JSON.stringify(data))

      localStorage.setItem('fundraiserId', fundraiserId ?? userId ?? null)
      // Keeps the grant available across the remaining form steps and a mid-form reload.
      storeFormToken(formToken)
      localStorage.setItem('campaign', JSON.stringify(campaign.value) ?? null)
      const from_qrcode = route.query.from_qrcode !== undefined
      localStorage.setItem('qrCode', JSON.stringify(from_qrcode))

      // If campaign does NOT have monthly frequency, skip terms page and go directly to contact or payment
      if (!hasMonthlyFrequency.value) {
        // Determine next step based on payment provider
        if (
          paymentProviders[paymentProvider.value] === SpringBoard ||
          paymentProviders[paymentProvider.value] === EngagingNetwork
        ) {
          // These providers need contact info first
          getStepNumber(1.5)
        } else if (paymentProviders[paymentProvider.value] !== EveryAction) {
          // Skip to payment page
          getStepNumber(2)
        } else {
          // EveryAction goes to contact
          getStepNumber(1)
        }
      }
      /**
       * HACK - You can use this to shortcut the form and submit manual data
       */
      // localStorage.setItem('submissionData', JSON.stringify(sample))
      // router.push({ name: 'ThankYou' })
      // await onSubmitFormsAlt(sample, fundraiserId ?? userId ?? null)
    }
    function getContactData(data) {
      const currentData = JSON.parse(localStorage.getItem('DonorData')) || {}
      const existingDonor = currentData?.donor ?? currentData
      const donor = {
        ...existingDonor,
        ...data,
      }
      emailOptIn.value = donor.isEmailPreferred
      mobilePhone.value = donor.mobilePhone
      email.value = donor.email
      localStorage.setItem('DonorData', JSON.stringify({ donor }))
    }

    onUnmounted(() => {
      console.log('Unmounted')
      everyActionProvider.removeFormAndScripts()
    })

    return {
      setPersonalData,
      age,
      setUserAge,
      lowestAge,
      filteredFrequencies,
      userToken,
      userId,
      campaign,
      npo,
      donationFormOpacityStyle,
      DonorFormPersonal,
      DonorFormContact,
      DonorFormPayment,
      stepNumber,
      successModalOpen,
      failureModalOpen,
      qrCodeModalOpen,
      qrCodeImgUrl,
      campaignApiUrl,
      campaignId,
      onSubmitForms,
      toggleQRCodeModal,
      toggleSuccessModal,
      toggleFailureModal,
      donateFailureMessage,
      donateSuccessMessage,
      getStepNumber,
      getQRCode,
      campaignActive,
      loading,
      disabledMessage,
      showStopCounter,
      isQRCodeOnly,
      stopCount,
      stopCountLoading,
      changeStopCount,
      campaignName,
      emailOptIn,
      getMaskedPan,
      everyActionContainer,
      paymentProvider,
      getContactData,
      EveryAction,
      SpringBoard,
      NAACPCustomAPI,
      paymentProviders,
      onSubmitFormsAlt,
      acceptedTerms,
      termsAgreement,
      getEaFormData,
      EngagingNetwork,
      mobilePhone,
      email,
      QRCode,
      isOneTimeOnly,
      hasMonthlyFrequency,
      hasReturnedFromPaymentPage,
      backStepForCurrentStep,
      progressStep,
      personalFormKey,
      contactFormKey,
      paymentFormKey,
      isResubmitMode,
      personalPrefill,
      contactPrefill,
      paymentPrefill,
      resubmitFormReady,
      clearForm,
      bbmsCheckoutRetryMessage,
      BBMSCheckout,
      BBMSCheckoutInline,
      checkoutConfig,
      onBBMSCheckoutInlineStarted,
      onBBMSCheckoutInlineError,
      router,
    }
  },
}
</script>
